import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import {
  IGroupFilterType,
  corporateGroupTableConfigs,
  useVariables,
} from '../../../../variables'
import { IApiDataType, INestedDataType } from '../../../types'

interface IIntradayMetricValuesType {
  listLocationIntradayMetricValues: {
    nodes: {
      groupByStr: string
      locationId: string
      locationGroupId: string
      locationGroupName: string
      startDate: string
      endDate: string
      startMinute: number
      endMinute: number
      metricData: any
    }[]
  }
}

export const fwWingstopLocationIntradayConfigs = {
  ...corporateGroupTableConfigs,
  '<%- JSON(intradayTableColumns.price) %>': 'price',
  '<%- JSON(intradayTableColumns.number) %>': 'number',
  '<%- JSON(intradayTableColumns.percent) %>': 'percent',
} as const

export const fwWingstopLocationIntradayCsvConfigs = {
  ...corporateGroupTableConfigs,
  '<%- JSON(intradayCsvColumns.price) %>': 'price',
  '<%- JSON(intradayCsvColumns.number) %>': 'number',
  '<%- JSON(intradayCsvColumns.percent) %>': 'percent',
} as const

const intradayQuery = gql`
  query IntradayMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iParams: JSON!
  ) {
    listLocationIntradayMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: $iParams
    ) {
      nodes {
        groupByStr
        locationId
        locationGroupId
        locationGroupName
        startDate
        endDate
        startMinute
        endMinute
        metricData
      }
    }
  }
`

function parseDow(input: string): number | null {
  const match = input.match(/DOW:(\d)/)
  return match ? parseInt(match[1], 10) : null
}

const useFwWingstopLocationIntraday = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { variables } = useVariables()

  const variablesData = useMemo(
    () =>
      variables['corporateGroup'] as IGroupFilterType<
        'listLocationGroup' | 'listLocation' | 'location'
      >,
    [variables],
  )
  const locations =
    variablesData && 'locations' in variablesData
      ? variablesData.locations
      : undefined
  const locationGroups =
    variablesData && 'locationGroups' in variablesData
      ? variablesData.locationGroups
      : undefined

  const revenueCenterSelectorValue =
    variables?.intradayRevenueCenterMetricOptions?.value
  const hoursSelectorValue = variables?.intradayHoursOptions?.value
  const dowSelectorValue = variables?.intradayDayOfWeekOptions?.value

  let selectedMetric: string = ''
  let metricDisplayType: string = ''
  if (
    Array.isArray(revenueCenterSelectorValue?.[0]) &&
    revenueCenterSelectorValue[0].length > 0
  ) {
    const selectedValue = (
      revenueCenterSelectorValue[0][revenueCenterSelectorValue[0].length - 1] ||
      ''
    ).split('|')
    selectedMetric = selectedValue[0] || ''
    metricDisplayType = selectedValue[1] || ''
  }

  const selectedHours: {
    start_minute: number
    end_minute: number
  }[] = []

  if (Array.isArray(hoursSelectorValue) && hoursSelectorValue.length > 0) {
    hoursSelectorValue.forEach((valueArray) => {
      const timeString = (valueArray?.[0] || '').replace('hour', '')
      const paddedTime = timeString.padStart(4, '0')
      const hours = parseInt(paddedTime.substring(0, 2), 10)
      const minutes = parseInt(paddedTime.substring(2), 10)
      const startMinute = hours * 60 + minutes
      const endMinute = startMinute + 30
      selectedHours.push({ start_minute: startMinute, end_minute: endMinute })
    })
  }

  const selectedDayOfWeek: number[] = []
  if (Array.isArray(dowSelectorValue) && dowSelectorValue.length > 0) {
    dowSelectorValue.forEach((valueArray) => {
      const dowValue = parseInt((valueArray?.[0] || '').replace('dow', ''), 10)
      if (dowValue) {
        selectedDayOfWeek.push(dowValue)
      }
    })
  }

  const { data, loading } = useQuery<IIntradayMetricValuesType>(intradayQuery, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iParams: {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: [selectedMetric],
        group_by: [hasGroupBy ? 'LOCATION' : 'LOCATION_GROUP', 'DAY_OF_WEEK'],
        selected_day_of_week: selectedDayOfWeek,
        intraday_time_ranges: selectedHours,
      },
    },
    skip:
      !selectedMetric ||
      selectedHours.length <= 0 ||
      selectedDayOfWeek.length <= 0 ||
      !startDate ||
      !endDate ||
      !groupFilter,
  })

  const isDateRangeLessThanAWeek =
    moment(startDate).isValid() &&
    moment(endDate).isValid() &&
    moment(startDate).add(7, 'days').isAfter(moment(endDate))
  const isSummaryMetric = selectedMetric !== 'splh'
  const hasTotalRow = isDateRangeLessThanAWeek && isSummaryMetric

  return {
    data: useMemo((): IApiDataType => {
      if (!data) return null

      const resultData = data?.listLocationIntradayMetricValues?.nodes
      if (!resultData || resultData.length <= 0) return []

      const rowData: { [rowKey: string]: INestedDataType } = {}
      const summaryData: { [rowKey: string]: INestedDataType } = {}

      summaryData['total'] = {
        id: 'summary',
        parentId: 'root',
        location: {
          label: 'TOTAL',
        },
        locationGroup: {
          label: 'TOTAL',
        },
      }

      resultData.forEach((d: any) => {
        const dow = parseDow(d.groupByStr)

        const columnKey = `${metricDisplayType}_${dow}_${d.startMinute}`

        const rowKey = `${d.locationId}`

        const entityId = hasGroupBy ? d.locationId : d.locationGroupId
        const tableRow = (locations || locationGroups)?.[entityId]?.tableRow

        rowData[rowKey] = rowData[rowKey] || {
          id: entityId,
          parentId: hasTotalRow ? 'summary' : 'root',
          ...tableRow,
        }

        if (d.metricSummaryData?.[selectedMetric]?.value) {
          summaryData['total'][columnKey] =
            d.metricSummaryData?.[selectedMetric]?.value || null
        }

        rowData[rowKey][columnKey] =
          d.metricData?.[selectedMetric]?.value || null
        if (
          rowData[rowKey][columnKey] &&
          ['price', 'percent'].includes(metricDisplayType)
        ) {
          rowData[rowKey][columnKey] =
            Number(rowData[rowKey][columnKey]) * 100.0
        }
      })

      if (hasTotalRow) {
        Object.values(rowData).forEach((row) => {
          Object.keys(row).forEach((key) => {
            if (key.startsWith(metricDisplayType)) {
              summaryData['total'][key] =
                (summaryData['total'][key] || 0) + row[key]
            }
          })
        })

        return [...Object.values(rowData), ...Object.values(summaryData)]
      }

      return Object.values(rowData)
    }, [data, selectedMetric, selectedHours, startDate, endDate, groupFilter]),
    loading,
  }
}

export default useFwWingstopLocationIntraday
